/** turf.js - Thin wrappers for Turf functions that catch exceptions and log enough data to reproduce them.
 * Turf API docs - http://turfjs.org/docs/
 *
 * Anti-pattern: DON'T send "nice" return values (e.g. empty GeoJson feature) after exceptions to make calling
 *               code work. We want calling code to deal with failures.
 * Anti-pattern: DON'T add higher-level functionality--Geojson.js and other modules are for that.
 */

import turfArea from '@turf/area';
import turfBbox from '@turf/bbox';
import turfBooleanClockwise from '@turf/boolean-clockwise';
import turfCentroid from '@turf/centroid';
import turfDifference from '@turf/difference';
import turfEqual from '@turf/boolean-equal';
import turfIntersect from '@turf/intersect';
import turfKinks from '@turf/kinks';
import turfRewind from '@turf/rewind';
import turfUnkinkPolygon from '@turf/unkink-polygon';
import { point as turfPoint } from '@turf/helpers';
import turfCircle from '@turf/circle';
import turfLineIntersect from '@turf/line-intersect';
import turfUnion from '@turf/union';

import { clippingLibrary } from 'geometry/geojson/constants';
import { guardedCall } from './guarded-call';
import { isValidGeoJson } from 'geometry/geojson/validator';

export const library = clippingLibrary.TURF;

/* Use calculateOverlap() instead of intersect(), calculateUnion() instead of union(), calculateDifference()
   instead of difference(). You will be insulated from known issues and upcoming changes to 3rd-party libraries. */

const geoJsonResultValidator = (geoJson, logMessageFunc) => {
  const failures = [];
  if (geoJson && !isValidGeoJson(geoJson, failures))
  {

    /* TODO - this is a hack to get the drawing tools cancellations to be less frequent at the expense of
       allowing possible corruptions into scripts. (SC-2525) It should not remain in the code. See commits prior
       to 3/6/18 for proper handling, e.g. throw an exception, don't use logMessageFunc(). */
    logMessageFunc(new Error(`bad geoJson returned from API: ${failures.join('\n')}`));
  }
};

// Add wrappers below as needed.
export function area()
{ return guardedCall({func:turfArea, args:[...arguments], library, funcName:'area'}); }
export function bbox()
{ return guardedCall({func:turfBbox, args:[...arguments], library, funcName:'bbox'}); }
export function booleanClockwise() { return guardedCall({func:turfBooleanClockwise, args:[...arguments],
  library, funcName:'booleanClockwise'}); }
export function centroid()
{ return guardedCall({func: turfCentroid, args: [...arguments], library, funcName: 'centroid'}); }
export function circle() {
  return guardedCall({func:turfCircle, args:[...arguments], library, funcName:'circle',
    onValidateResult:geoJsonResultValidator});
}
export function difference()
{
  return guardedCall({func:turfDifference, args:[...arguments], library, funcName:'difference',
    onValidateResult:geoJsonResultValidator, translateUndefined:null});
}
export function equal()
{ return guardedCall({func:turfEqual, args:[...arguments], library, funcName:'equal'}); }
export function intersect() {
  // intersect() validation is complicated--let existing code in calculateOverlapTurf() handle it for now.
  return guardedCall({func:turfIntersect, args:[...arguments], library, funcName:'intersect',
    translateUndefined:null});
}
export function kinks()
{ return guardedCall({func:turfKinks, args:[...arguments], library, funcName:'kinks'}); }
export function lineIntersect()
{ return guardedCall({func:turfLineIntersect, args:[...arguments], library, funcName:'lineIntersect'}); }
export function point()
{ return guardedCall({func:turfPoint, args:[...arguments], library, funcName:'point'}); }
export function rewind()
{ return guardedCall({func:turfRewind, args:[...arguments], library, funcName:'rewind'}); }
export function union()
{
  return guardedCall({func:turfUnion, args:[...arguments], library, funcName:'union',
    onValidateResult:geoJsonResultValidator});
}
export function unkinkPolygon()
{ return guardedCall({func:turfUnkinkPolygon, args:[...arguments], library, funcName:'unkinkPolygon'}); }
